import React, { ButtonHTMLAttributes, AnchorHTMLAttributes, ClassAttributes } from "react";
import classNames from "classnames";

export type ButtonSize = "lg" | "md" | "sm" | "icon";
export type ButtonFluidBreakpoints = "md" | "sm";

export const legacyButtonColors = ["orange", "ocean", "gray", "white", "link", "light-gray", "light-orange", "transparent"];
type LegacyButtonColor = typeof legacyButtonColors[number];
type CurrentButtonColor = "primary" | "primary-on-ocean" | "primary-on-dark" | "secondary" | "secondary-on-ocean" | "secondary-on-dark" | "clean" | "arctic-20" | "icon" | "icon-outline" | "primary-link" | "input";
export type ButtonColor = CurrentButtonColor | LegacyButtonColor;

export type CustomButtonProps = {
   size?: ButtonSize,
   fluid?: boolean | ButtonFluidBreakpoints,
   color?: ButtonColor,
   outline?: boolean,
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & ClassAttributes<HTMLButtonElement> & CustomButtonProps;
export type ButtonLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & CustomButtonProps;

const getBtnClasses = (buttonProps: CustomButtonProps, className: string | undefined): string => {
   const outline = !buttonProps.color
      ? buttonProps.outline
      : (buttonProps.outline && legacyButtonColors.includes(buttonProps.color));

   return classNames("btn", className, {
      [`btn-${buttonProps.size}`]: !!buttonProps.size,
      "btn--fluid": typeof buttonProps.fluid == "boolean" && buttonProps.fluid,
      [`btn--fluid-${buttonProps.fluid}`]: typeof buttonProps.fluid != "boolean" && buttonProps.fluid,
      [`btn-${buttonProps.color}`]: !outline && buttonProps.color,
      "btn-outline": outline && !buttonProps.color,
      [`btn-outline-${buttonProps.color}`]: outline && buttonProps.color,
   });
}

export const Button: React.FC<ButtonProps> = React.forwardRef<HTMLButtonElement, ButtonProps>(({ children, size, className, color, outline = false, fluid = false, ...htmlProps }, ref) => {

   const buttonClasses = getBtnClasses({
      size,
      fluid,
      color,
      outline
   }, className);

   return (<button ref={ref} className={buttonClasses} {...htmlProps}>{children}</button>)
});

export const ButtonLink: React.FC<ButtonLinkProps> = ({ children, size, className, color, outline = false, fluid = false, ...htmlProps }) => {
   const buttonClasses = getBtnClasses({
      size,
      fluid,
      color,
      outline
   }, className);

   return (<a className={buttonClasses} {...htmlProps}>{children}</a>)
}

export const PlayButton: React.FC<ButtonHTMLAttributes<HTMLButtonElement>> = ({ children, ...htmlProps }) => {
   return (
      <button className="play-btn" {...htmlProps}>
         <div className="play-btn--icon"></div>
         <div className="play-btn--text">{children}</div>
      </button>)
}

export type SpinningButtonProps = ButtonProps & {
   isLoading: boolean
}
export const SpinningButton: React.FC<SpinningButtonProps> = ({ isLoading, className, children, ...buttonProps }) => {
   const buttonClasses = classNames({
      "spinning-btn": true,
      "spinning-btn--spinning": isLoading,
   }, className);
   return (
      <Button className={buttonClasses} {...buttonProps}>
         <span className="spinning-btn-text">{children}</span>
         <span className="spinning-btn-spinner spinner-border spinner-border-sm"></span>
      </Button>
   )
}

export { default as ButtonGroup } from "reactstrap/lib/ButtonGroup";
